<template>
  <a-modal :visible="visible" title="结算" :maskClosable="false" width="460px"
           :confirmLoading="loading" :okText="okText" cancelText="取消"
           :footer="qrShowing ? null : undefined"
           @ok="handleOk" @cancel="handleCancel">
    <div class="pay-body">
      <!-- 扫码收款态：仅展示收款二维码 -->
      <template v-if="qrShowing">
        <div class="pay-total">
          收款金额：¥<span class="pay-total-num">{{ format(payable) }}</span>
        </div>
        <div class="qr-box">
          <img v-if="qrBase64" class="qr-img" :src="'data:image/png;base64,' + qrBase64" alt="收款码" />
          <div class="qr-tip">{{ qrExpired ? '收款码已超时，请重新生成' : '请顾客使用微信/支付宝扫码支付' }}</div>
          <div class="qr-actions">
            <a-button size="small" @click="emitCreateQrcode">重新生成</a-button>
            <a-button size="small" @click="handleCancel">取消</a-button>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="pay-total">
          应收金额：¥<span class="pay-total-num">{{ format(payable) }}</span>
        </div>
        <div class="pay-row">
          <span class="pay-label">支付方式：</span>
          <div class="pay-types">
            <div v-for="type in payOptions" :key="type" class="pay-type"
                 :class="{ active: payTypes.includes(type) }" @click="toggleType(type)">{{ type }}</div>
          </div>
        </div>
        <!-- 单选微信/支付宝：真实支付收款模式 -->
        <template v-if="singleOnline">
          <div class="pay-row">
            <span class="pay-label">收款模式：</span>
            <div class="pay-types">
              <div class="pay-type" :class="{ active: payMode === 'bar' }" @click="payMode = 'bar'">付款码支付</div>
              <div class="pay-type" :class="{ active: payMode === 'qrcode' }" @click="payMode = 'qrcode'">扫码收款</div>
            </div>
          </div>
          <div class="pay-row" v-if="payMode === 'bar'">
            <span class="pay-label">顾客付款码：</span>
            <a-input ref="authInput" v-model="authCode" placeholder="扫码枪录入后自动支付"
                     style="width: 220px" @pressEnter="submitBar" />
          </div>
          <div class="pay-row pay-mode-tip" v-else>
            <span class="pay-sub">点击「生成收款码」后请顾客扫码支付，支付完成自动结算</span>
          </div>
        </template>
        <div class="pay-row">
          <span class="pay-label">抹零金额：</span>
          <a-input-number v-model="eraseAmount" :min="0" :max="total" :precision="2" style="width: 140px" />
          <span class="pay-sub">（可选，分位抹零）</span>
        </div>
        <div class="pay-row" v-if="payTypes.includes('现金')">
          <span class="pay-label">现金收款：</span>
          <a-input-number v-model="cashReceived" :min="0" :precision="2" style="width: 140px"
                          placeholder="实收现金" />
          <span class="pay-sub" v-if="cashReceived > 0">找零：¥{{ format(Math.max(0, cashReceived - payable)) }}</span>
        </div>
        <div class="pay-row">
          <span class="pay-label">需要发票：</span>
          <a-checkbox :checked="needInvoice" @change="e => needInvoice = e.target.checked" />
          <template v-if="needInvoice">
            <a-input v-model="invoiceTitle" placeholder="发票抬头" style="width: 180px; margin-left: 8px" />
          </template>
        </div>
      </template>
    </div>
  </a-modal>
</template>

<script>
  import { cashierPayStatus, cashierPayCancel } from '@/api/plugin/member'

  const QR_TIMEOUT_MS = 5 * 60 * 1000
  const POLL_INTERVAL_MS = 3000

  /**
   * 结算弹窗：支付方式单选（现金 / 移动支付）+ 抹零 + 找零
   * 移动支付（微信/支付宝自动识别渠道，结算账户自动对应，无需用户选择）：
   *   付款码支付（B扫C）：扫码枪录入顾客付款码自动提交 /cashier/pay-bar
   *   扫码收款（C扫B）：生成收款二维码 3 秒轮询 /cashier/pay-status，成功后自动结算并关闭弹窗
   */
  export default {
    name: 'PayModal',
    props: {
      visible: { type: Boolean, default: false },
      total: { type: Number, default: 0 },
      loading: { type: Boolean, default: false },
      // 收款二维码数据 {payOrderNo, qrBase64}（CashierDesk 经 /cashier/pay-qrcode 预下单返回）
      payQr: { type: Object, default: null }
    },
    data() {
      return {
        payOptions: ['现金', '移动支付'],
        payTypes: ['移动支付'],
        eraseAmount: 0,
        cashReceived: 0,
        needInvoice: false,
        invoiceTitle: '',
        // 真实支付状态
        payMode: 'bar',
        authCode: '',
        authTimer: null,
        qrBase64: '',
        payOrderNo: null,
        qrExpired: false,
        qrPaid: false,
        pollTimer: null,
        pollStartAt: 0,
        pollChecking: false
      }
    },
    computed: {
      payable() {
        return this.total - this.eraseAmount
      },
      singleOnline() {
        return this.payTypes.length === 1 && this.payTypes[0] === '移动支付'
      },
      qrShowing() {
        return !!this.payQr && !!this.payQr.payOrderNo
      },
      okText() {
        if (this.singleOnline && this.payMode === 'qrcode') {
          return '生成收款码'
        }
        if (this.singleOnline && this.payMode === 'bar') {
          return '确认支付'
        }
        return '确认结算'
      }
    },
    watch: {
      visible(val) {
        if (val) {
          this.payTypes = ['移动支付']
          this.eraseAmount = 0
          this.cashReceived = 0
          this.needInvoice = false
          this.invoiceTitle = ''
          this.payMode = 'bar'
          this.authCode = ''
          clearTimeout(this.authTimer)
          this.stopPolling()
        } else {
          this.resetQrState()
        }
      },
      payQr(val) {
        if (val && val.payOrderNo) {
          this.qrBase64 = val.qrBase64 || ''
          this.payOrderNo = val.payOrderNo
          this.qrExpired = false
          this.qrPaid = false
          this.startPolling()
        }
      },
      // 扫码枪无回车后缀时按长度自动提交（微信 18 位：10~15 开头；支付宝 25~30 位：25/26/28/29/30 开头）
      authCode(val) {
        const code = (val || '').trim()
        if (/^1[0-5]\d{16}$/.test(code) || /^(25|26|28|29|30)\d{23,28}$/.test(code)) {
          clearTimeout(this.authTimer)
          this.authTimer = setTimeout(() => {
            if (code === (this.authCode || '').trim()) {
              this.submitBar()
            }
          }, 300)
        }
      },
      singleOnline(val) {
        if (val && this.payMode === 'bar') {
          this.focusAuthInput()
        }
      },
      payMode() {
        if (this.payMode === 'bar' && this.singleOnline) {
          this.focusAuthInput()
        }
      }
    },
    beforeDestroy() {
      this.stopPolling()
      clearTimeout(this.authTimer)
    },
    methods: {
      format(price) {
        return Number(price || 0).toFixed(2)
      },
      toggleType(type) {
        // 单选：现金 / 移动支付（微信支付宝自动识别，无需用户选择）
        this.payTypes = [type]
      },
      focusAuthInput() {
        this.$nextTick(() => {
          const input = this.$refs.authInput
          if (input && input.focus) {
            input.focus()
          }
        })
      },
      buildSettlePayload() {
        return {
          payTypes: [...this.payTypes],
          eraseAmount: this.eraseAmount,
          invoiceTitle: this.needInvoice ? this.invoiceTitle.trim() : null
        }
      },
      handleOk() {
        if (!this.payTypes.length) {
          this.$message.warning('请选择支付方式')
          return
        }
        if (this.needInvoice && !(this.invoiceTitle || '').trim()) {
          this.$message.warning('请输入发票抬头')
          return
        }
        if (this.payable < 0) {
          this.$message.warning('抹零金额不能超过合计')
          return
        }
        if (this.singleOnline) {
          if (this.payMode === 'qrcode') {
            this.emitCreateQrcode()
            return
          }
          this.submitBar()
          return
        }
        this.$emit('ok', Object.assign(this.buildSettlePayload(), {
          change: this.payTypes.includes('现金') && this.cashReceived > 0
            ? Math.max(0, this.cashReceived - this.payable) : 0
        }))
      },
      // 付款码支付（B扫C）：提交 /cashier/pay-bar
      submitBar() {
        const code = (this.authCode || '').trim()
        if (!code) {
          this.$message.warning('请扫描顾客付款码')
          return
        }
        clearTimeout(this.authTimer)
        this.authCode = ''
        this.$emit('ok', Object.assign(this.buildSettlePayload(), {
          payMode: 'bar',
          authCode: code
        }))
      },
      // 付款码被网关拒绝时自动切扫码收款模式（B扫C → C扫B）
      switchToQrcode() {
        this.authCode = ''
        this.payMode = 'qrcode'
      },
      // 扫码收款（C扫B）：由 CashierDesk 预下单生成二维码
      emitCreateQrcode() {
        this.$emit('create-qrcode', Object.assign(this.buildSettlePayload(), {
          closePayOrderNo: this.payOrderNo || undefined
        }))
      },
      handleCancel() {
        this.$emit('cancel')
      },
      startPolling() {
        this.stopPolling()
        this.pollStartAt = Date.now()
        this.pollTimer = setInterval(() => {
          if (Date.now() - this.pollStartAt >= QR_TIMEOUT_MS) {
            this.stopPolling()
            this.qrExpired = true
            return
          }
          this.checkPayStatus()
        }, POLL_INTERVAL_MS)
      },
      stopPolling() {
        if (this.pollTimer) {
          clearInterval(this.pollTimer)
          this.pollTimer = null
        }
      },
      async checkPayStatus() {
        if (this.pollChecking || !this.payOrderNo) {
          return
        }
        this.pollChecking = true
        try {
          const res = await cashierPayStatus(this.payOrderNo)
          if (!res || res.code !== 200 || !res.data) {
            return
          }
          const status = String(res.data.payStatus)
          if (status === '1') {
            this.qrPaid = true
            this.stopPolling()
            this.$message.success('支付成功')
            this.$emit('ok', Object.assign(this.buildSettlePayload(), {
              payMode: 'qrcode',
              payOrderNo: this.payOrderNo
            }))
          } else if (status === '2') {
            this.stopPolling()
            this.qrExpired = true
            this.$message.warning('支付单已关闭，请重新生成收款码')
          }
        } catch (e) {
          // 单次轮询异常忽略，下轮重试
        } finally {
          this.pollChecking = false
        }
      },
      resetQrState() {
        this.stopPolling()
        clearTimeout(this.authTimer)
        // 尽力关闭未支付单（已支付单后端不动），防旧码被顾客支付后无法结算
        if (this.payOrderNo && !this.qrPaid) {
          cashierPayCancel(this.payOrderNo).catch(() => {})
        }
        this.qrBase64 = ''
        this.payOrderNo = null
        this.qrExpired = false
        this.qrPaid = false
      }
    }
  }
</script>

<style scoped>
  .pay-total {
    text-align: center;
    font-size: 15px;
    color: #555;
    margin-bottom: 18px;
  }
  .pay-total-num {
    font-size: 32px;
    color: #e4393c;
    font-weight: 700;
  }
  .pay-row {
    display: flex;
    align-items: center;
    margin-bottom: 14px;
  }
  .pay-label {
    width: 90px;
    text-align: right;
    margin-right: 8px;
    color: #555;
  }
  .pay-sub {
    color: #999;
    font-size: 12px;
    margin-left: 8px;
  }
  .pay-types {
    display: flex;
    gap: 8px;
  }
  .pay-type {
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    padding: 5px 16px;
    cursor: pointer;
    font-size: 13px;
    color: #555;
    user-select: none;
  }
  .pay-type.active {
    background: #0081ff;
    border-color: #0081ff;
    color: #fff;
  }
  .pay-mode-tip {
    margin-top: -6px;
  }
  .qr-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-bottom: 8px;
  }
  .qr-img {
    width: 240px;
    height: 240px;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
  }
  .qr-tip {
    margin-top: 12px;
    color: #555;
    font-size: 13px;
  }
  .qr-actions {
    margin-top: 12px;
    display: flex;
    gap: 12px;
  }
</style>
